import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { createWarning } from '@core/helpers/create-validator.helper';
import { toNum } from '@shared/helpers/math.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { AderentiAEnum } from '@shared/constants/aderenti-a.enum';

@Injectable()
export class ContribLegge5992WarningsService {
    constructor(private readonly store: Store<Revisione53State>) {}

    my_continuitaNelleRevisioni = createWarning(this.store, (myVerbale) => {
        const my_continuitaNelleRevisioni = myVerbale.my_continuitaNelleRevisioni;
        const warningMessage =
            "Attenzione, verificare e allegare al verbale la situazione contributiva come da sito MiSE. Nell'impossibilità di recupero richiedere documenti attestanti versamenti effettuati o, in caso di mancata dimostrazione, dichiarazione sostitutiva di atto notorio sottoscritta dal legale rappresentante.\n";
        return my_continuitaNelleRevisioni !== 'S' ? warningMessage : null;
    });

    cooperativaAderenteACoonf = createWarning(this.store, (myVerbale) => {
        return this._cooperativaAderenteACoonfHelper(myVerbale, false);
    });

    cooperativaAderenteACoonfAnno1 = createWarning(this.store, (myVerbale) => {
        return this._cooperativaAderenteACoonfHelper(myVerbale, true);
    });

    my_EventAccantUtiRisInd = createWarning(this.store, (myVerbale) => {
        const my_BilAnno2 = toNum(myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno2);
        const my_EventAccantUtiRisInd = toNum(myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_EventAccantUtiRisInd);
        const my_RisultEsercizio = toNum(myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_RisultEsercizio);

        return this._eventAccantUtiRisIndHelper(my_BilAnno2, my_EventAccantUtiRisInd, my_RisultEsercizio);
    });

    my_EventAccantUtiRisIndAnno1 = createWarning(this.store, (myVerbale) => {
        const my_BilAnno1 = toNum(myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno1);
        const my_EventAccantUtiRisIndAnno1 = toNum(myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_EventAccantUtiRisIndAnno1);
        const my_RisultEsercizioAnno1 = toNum(myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_RisultEsercizioAnno1);

        return this._eventAccantUtiRisIndHelper(my_BilAnno1, my_EventAccantUtiRisIndAnno1, my_RisultEsercizioAnno1);
    });

    my_ImportoVersatoLabel = createWarning(this.store, (myVerbale) => {
        const my_BilAnno2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno2;
        const my_data_immatricolazione_cci = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_data_immatricolazione_cci;
        const condition = my_data_immatricolazione_cci.substring(0, 4) === my_BilAnno2 && !isNilOrEmpty(my_BilAnno2) && !isNilOrEmpty(my_data_immatricolazione_cci);
        return condition ? 'Attenzione, il contributo è dovuto solo parzialmente a Fondosviluppo' : null;
    });

    my_ImportoComplessivo = createWarning(this.store, (myVerbale) => {
        const leg59 = myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59;
        const versatoRaw = leg59.my_ImportoVersato;
        const complessivoRaw = leg59.my_ImportoComplessivo;
        const versato = toNum(versatoRaw);
        const complessivo = toNum(complessivoRaw);
        if (!isNilOrEmpty(versatoRaw) && !isNilOrEmpty(complessivoRaw) &&
            Math.floor(versato) < Math.floor(complessivo) && complessivo >= 10.33) {
            return 'Attenzione: la cooperativa non ha versato il contributo dovuto ai sensi dell\'art. 11 L. 59/92';
        }
        return null;
    });

    my_ImportoVersato = createWarning(this.store, (myVerbale) => {
        const leg59 = myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59;
        const versatoRaw = leg59.my_ImportoVersato;
        const complessivoRaw = leg59.my_ImportoComplessivo;
        const versato = toNum(versatoRaw);
        const complessivo = toNum(complessivoRaw);
        if (!isNilOrEmpty(versatoRaw) && !isNilOrEmpty(complessivoRaw) &&
            Math.floor(versato) < Math.floor(complessivo) && complessivo >= 10.33) {
            return 'Attenzione: la cooperativa non ha versato il contributo dovuto ai sensi dell\'art. 11 L. 59/92';
        }
        if (versato > 0 && versato > complessivo && versato - complessivo > 1) {
            return 'Attenzione, prendere contatto con il Responsabile revisione.';
        }
        return null;
    });

    my_ImportoVersatoAnno1 = createWarning(this.store, (myVerbale) => {
        const my_ImportoVersatoAnno1 = toNum(myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_ImportoVersatoAnno1);
        const my_ImportoComplessivoAnno1 = toNum(myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_ImportoComplessivoAnno1);

        return this._importoVersatoHelper(my_ImportoVersatoAnno1, my_ImportoComplessivoAnno1);
    });

    private _eventAccantUtiRisIndHelper(bilAnno: number, eventAccantUtiRisInd: number, risultEsercizio: number): string {
        const baseCondition = eventAccantUtiRisInd >= risultEsercizio * 0.3 || risultEsercizio <= 0;
        const baseMessage =
            "Ai fini della verifica il revisore inserisca il valore derivante dalla delibera di destinazione dell'utile che comunque deve essere superiore al 30% degli utili";
        const deveRicordare =
            '\nIl revisore DEVE ricordare alla cooperativa che in presenza di utile, almeno il 30% deve essere destinato a riserva.\n';
        if (!(bilAnno < 2015 || baseCondition)) {
            return baseMessage + deveRicordare;
        }
        if (!(bilAnno < 2014 || baseCondition)) {
            return deveRicordare.trim();
        }
        return null;
    }

    private _importoVersatoHelper(importoVersato: number, importoComplessivo: number): string {
        const condition = importoVersato > 0 && importoVersato > importoComplessivo && importoVersato - importoComplessivo > 1;
        return condition ? 'Attenzione, prendere contatto con il Responsabile revisione.' : null;
    }

    private _cooperativaAderenteACoonfHelper(myVerbale: MyVerbale, isFirstYear: boolean): string {
        const aderentiAList = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_AderentiA?.my_ElAderentiA;
        const aderentiACoonfItem = aderentiAList.find((a) => a.my_SezVisibAderenteA?.my_CkAderenteA.toUpperCase() === AderentiAEnum.confcooperative.toString());
        const bilAnno = isFirstYear ? myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno1 : myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno2;
        const my_ImportoComplessivo = isFirstYear
            ? myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_ImportoComplessivoAnno1
            : myVerbale.my_SezioneI__Rilevazione.my_ContributoLegge59.my_ImportoComplessivo;
        if (
            !(
                aderentiACoonfItem?.my_SezVisibAderenteA?.my_IdAderenteA !== '1' ||
                bilAnno !== aderentiACoonfItem?.my_SezVisibAderenteA?.my_aderenteDal?.substring(0, 4) ||
                toNum(my_ImportoComplessivo) < 10.33
            )
        ) {
            return "Attenzione, cooperativa aderente a Confcooperative durante l'esercizio.";
        } else {
            return null;
        }
    }
}
